import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { GroupsState } from "./types";
import { fetchEmployeesGroups, fetchUsersGroups } from "./groups-thunk";
import { ErrorType } from "@/shared/types";
import { IGroup } from "@/shared/api/groups";
import { RoleType } from "@/shared/api/users";

const initialState: GroupsState = {
    usersGroups: [],
    isUsersGroupsLoading: false,
    usersGroupsError: null,
    employeesGroups: [],
    isEmployeesGroupsLoading: false,
    employeesGroupsError: null
}

const groupsSlice = createSlice({
    name: 'groups',
    initialState,
    reducers: {
        addGroup: (state, action: PayloadAction<{ group: IGroup; role: RoleType }>) => {
            const { group, role } = action.payload;
            if (role === 'users') {
                state.usersGroups.push(group);
            } else {
                state.employeesGroups.push(group);
            }
        },
        removeGroup: (state, action: PayloadAction<{ groupId: string; role: RoleType }>) => {
            const { groupId, role } = action.payload;
            if (role === 'users') {
                state.usersGroups = state.usersGroups.filter(g => g.id !== groupId);
            } else {
                state.employeesGroups = state.employeesGroups.filter(g => g.id !== groupId);
            }
        },
        addUserToGroup: (state, action: PayloadAction<{ groupId: string; user: { id: string; username: string | null }; role: RoleType }>) => {
            const { groupId, user, role } = action.payload;
            const groups = role === 'users' ? state.usersGroups : state.employeesGroups;
            const group = groups.find(g => g.id === groupId);
            if (group && !group.users.some(u => u.id === user.id)) {
                group.users.push(user);
            }
        },
        removeUserFromGroup: (state, action: PayloadAction<{ groupId: string; userId: string; role: RoleType }>) => {
            const { groupId, userId, role } = action.payload;
            const groups = role === 'users' ? state.usersGroups : state.employeesGroups;
            const group = groups.find(g => g.id === groupId);
            if (group) {
                group.users = group.users.filter(u => u.id !== userId);
            }
        },
        updateGroupParent: (state, action: PayloadAction<{ groupId: string; parentId: string | null; role: RoleType }>) => {
            const { groupId, parentId, role } = action.payload;
            const groups = role === 'users' ? state.usersGroups : state.employeesGroups;
            const group = groups.find(g => g.id === groupId);
            if (group) {
                group.parent_id = parentId;
            }
        },
    },
    extraReducers(builder) {
        builder
        .addCase(fetchUsersGroups.pending, (state) => {
            state.isUsersGroupsLoading = true
            state.usersGroupsError = null
        })
        .addCase(fetchUsersGroups.fulfilled, (state, action) => {
            state.isUsersGroupsLoading = false
            state.usersGroups = action.payload
            state.usersGroupsError = null
        })
        .addCase(fetchUsersGroups.rejected, (state, action) => {
            state.isUsersGroupsLoading = false
            state.usersGroupsError = action.payload as ErrorType ?? null
        })
        .addCase(fetchEmployeesGroups.pending, (state) => {
            state.isEmployeesGroupsLoading = true
            state.employeesGroupsError = null  
        })
        .addCase(fetchEmployeesGroups.fulfilled, (state, action) => {
            state.isEmployeesGroupsLoading = false
            state.employeesGroups = action.payload
            state.employeesGroupsError = null
        })
        .addCase(fetchEmployeesGroups.rejected, (state, action) => {
            state.isEmployeesGroupsLoading = false
            state.employeesGroupsError = action.payload as ErrorType ?? null
        })
    },
})

export default groupsSlice.reducer
export const { addGroup, removeGroup, addUserToGroup, removeUserFromGroup, updateGroupParent } = groupsSlice.actions